import axios from 'axios';

// Use BACKEND_URL or FRONTEND_SERVER_URL based on environment
const USE_FRONTEND_LOG_SERVER = process.env.REACT_APP_USE_FRONTEND_LOG_SERVER === 'true';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const FRONTEND_SERVER_URL = process.env.REACT_APP_FRONTEND_SERVER_URL || 'http://localhost:3000';

const LOG_ENDPOINT = USE_FRONTEND_LOG_SERVER 
    ? `${FRONTEND_SERVER_URL}/log` 
    : `${BACKEND_URL}/log/frontend`;
const BATCH_LOG_ENDPOINT = USE_FRONTEND_LOG_SERVER 
    ? `${FRONTEND_SERVER_URL}/log/batch` 
    : `${BACKEND_URL}/log/frontend/batch`;

// Queue for batching logs
let logQueue = [];
let batchTimer = null;
const BATCH_INTERVAL = 5000; // Send logs every 5 seconds
const MAX_BATCH_SIZE = 50;

/**
 * Get current user info from localStorage and sessionStorage
 */
const getUserInfo = () => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userCode = sessionStorage.getItem('code') || null;
        return {
            userEmail: user.email || null,
            userId: user._id || null,
            userCode: userCode
        };
    } catch {
        return {
            userEmail: null,
            userId: null,
            userCode: sessionStorage.getItem('code') || null
        };
    }
};

/**
 * Send log to backend with PHP fallback
 */
const sendLog = async (logData) => {
    try {
        // Try backend first
        await axios.post(LOG_ENDPOINT, logData, {
            timeout: 5000,
            headers: { 'Content-Type': 'application/json' }
        });
    } catch (error) {
        // Backend failed, try PHP fallback
        try {
            const fallbackUrl = window.location.origin + '/log-fallback.php';
            await axios.post(fallbackUrl, logData, {
                timeout: 3000,
                headers: { 'Content-Type': 'application/json' }
            });
            console.log('Log sent to PHP fallback');
        } catch (fallbackError) {
            // Both failed, fail silently
            console.error('Failed to send log to backend and fallback:', error.message, fallbackError.message);
        }
    }
};

/**
 * Send batch of logs to backend with PHP fallback
 */
const sendBatchLogs = async () => {
    if (logQueue.length === 0) return;

    const logsToSend = [...logQueue];
    logQueue = [];

    try {
        // Try batch send to backend first
        await axios.post(BATCH_LOG_ENDPOINT, { events: logsToSend }, {
            timeout: 10000,
            headers: { 'Content-Type': 'application/json' }
        });
    } catch (error) {
        console.error('Failed to send batch logs to backend, trying fallback:', error.message);
        
        // Fallback: send each log individually with PHP fallback
        for (const logData of logsToSend) {
            try {
                const fallbackUrl = window.location.origin + '/log-fallback.php';
                await axios.post(fallbackUrl, logData, {
                    timeout: 3000,
                    headers: { 'Content-Type': 'application/json' }
                });
            } catch (fallbackError) {
                console.error('Failed to send log to PHP fallback:', fallbackError.message);
            }
        }
    }
};

/**
 * Add log to queue
 */
const queueLog = (logData) => {
    logQueue.push(logData);

    // Send immediately if queue is full
    if (logQueue.length >= MAX_BATCH_SIZE) {
        clearTimeout(batchTimer);
        sendBatchLogs();
        return;
    }

    // Otherwise, schedule batch send
    if (!batchTimer) {
        batchTimer = setTimeout(() => {
            sendBatchLogs();
            batchTimer = null;
        }, BATCH_INTERVAL);
    }
};

/**
 * Create log data object
 */
const createLogData = (level, message, type, additionalData = {}) => {
    const userInfo = getUserInfo();
    
    return {
        level,
        message,
        type,
        ...userInfo,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        metadata: {
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            language: navigator.language,
            platform: navigator.platform,
            ...additionalData
        }
    };
};

/**
 * Logger class
 */
class FrontendLogger {
    constructor() {
        this.setupErrorHandlers();
    }

    /**
     * Setup global error handlers
     */
    setupErrorHandlers() {
        // Catch unhandled errors
        window.addEventListener('error', (event) => {
            this.error(event.message, 'unhandled_error', {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                stackTrace: event.error?.stack
            });
        });

        // Catch unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.error(
                event.reason?.message || String(event.reason),
                'unhandled_rejection',
                {
                    stackTrace: event.reason?.stack
                }
            );
        });

        // Send logs on page unload
        window.addEventListener('beforeunload', () => {
            if (logQueue.length > 0) {
                sendBatchLogs();
            }
        });
    }

    /**
     * Log info message
     */
    info(message, type = 'info', additionalData = {}) {
        const logData = createLogData('info', message, type, additionalData);
        queueLog(logData);
        
        if (process.env.NODE_ENV === 'development') {
            console.log(`[INFO] ${message}`, additionalData);
        }
    }

    /**
     * Log warning message
     */
    warn(message, type = 'warning', additionalData = {}) {
        const logData = createLogData('warn', message, type, additionalData);
        queueLog(logData);
        
        if (process.env.NODE_ENV === 'development') {
            console.warn(`[WARN] ${message}`, additionalData);
        }
    }

    /**
     * Log error message
     */
    error(message, type = 'error', additionalData = {}) {
        const logData = createLogData('error', message, type, additionalData);
        
        // Send errors immediately (don't batch)
        sendLog(logData);
        
        if (process.env.NODE_ENV === 'development') {
            console.error(`[ERROR] ${message}`, additionalData);
        }
    }

    /**
     * Log debug message (only in development)
     */
    debug(message, type = 'debug', additionalData = {}) {
        if (process.env.NODE_ENV === 'development') {
            console.debug(`[DEBUG] ${message}`, additionalData);
        }
    }

    /**
     * Track user event/action
     */
    track(eventName, eventData = {}) {
        const logData = createLogData('info', eventName, 'analytics', {
            event: eventName,
            ...eventData
        });
        queueLog(logData);
        
        if (process.env.NODE_ENV === 'development') {
            console.log(`[TRACK] ${eventName}`, eventData);
        }
    }

    /**
     * Track page view
     */
    pageView(pageName, additionalData = {}) {
        this.track('page_view', {
            page: pageName,
            ...additionalData
        });
    }

    /**
     * Track PDF button click
     */
    pdfClick(reportType = 'unknown', additionalData = {}) {
        this.track('pdf_click', {
            reportType: reportType,
            ...additionalData
        });
    }

    /**
     * Track API error
     */
    apiError(endpoint, error, additionalData = {}) {
        this.error(
            `API Error: ${endpoint}`,
            'api_error',
            {
                endpoint,
                status: error.response?.status,
                statusText: error.response?.statusText,
                errorMessage: error.message,
                responseData: error.response?.data,
                ...additionalData
            }
        );
    }

    /**
     * Flush all queued logs immediately
     */
    flush() {
        clearTimeout(batchTimer);
        batchTimer = null;
        return sendBatchLogs();
    }
}

// Create singleton instance
const logger = new FrontendLogger();

export default logger;
